import React, { useState, useEffect, useCallback } from 'react';
import { useRootStore } from 'store';
import { useParams, useHistory, useLocation, generatePath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import {
  toastSuccess,
  FormCard,
  isNumeric,
  YupString,
  YupNumeric,
  YupBoolean,
  YupDate,
} from 'motion-components';
import { useHandleError } from 'shared-pages/services';
import { ButtonCloseModal } from 'shared-pages/components';
import { getRouteByName } from 'app/routes';

import { gqlGetByIdQuery, gqlAdd, gqlUpdate, gqlDelete } from '../graphql';
import QrCodeFormFields from './qrcodes-form-fields';
import { convertFieldToFloat } from 'shared-pages/common/form-helpfunc';

type Props = {
  isModal?: boolean;
  paramId?: string | undefined;
  modalOnClose?: (id: number) => void | undefined;
};

export const QrCodeFormPage = ({ isModal = false, paramId = undefined, modalOnClose }: Props) => {
  const { appStore } = useRootStore();
  const { id: urlId } = useParams<{ id: string }>();
  const id = isModal ? paramId : urlId;
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const history = useHistory();
  const location: any = useLocation();
  const { t } = useTranslation();
  const hasAccess = appStore.isPlatformOwner;

  const [initialNewRecData, setInitialNewRecData] = useState<any>();
  const [get, { data: rawData, error, refetch, loading }] = useLazyQuery(gqlGetByIdQuery);
  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);

  const { handleGraphqlError } = useHandleError('qrcode');

  const formShape = {
    id: Yup.number().nullable(),
    qrCode: YupString.required(t('Το πεδίο είναι υποχρεωτικό')),
    declaredDate: YupDate,
    binSerialNo: YupString,
    collectionPointId: Yup.number().required(t('Το πεδίο είναι υποχρεωτικό')),
    collectionpoint: Yup.object(),
    containerTypeId: Yup.number().required(t('Το πεδίο είναι υποχρεωτικό')),
    containertype: Yup.object(),
    latitude: YupNumeric.nullable(),
    longitude: YupNumeric.nullable(),
    comments: YupString,
    driverComments: YupString,

    isActive: YupBoolean,
    isNewCollectionpoint: YupBoolean,
    isCollectionpointClosed: YupBoolean,
    containerReplaced: YupBoolean,
  };

  const validationSchema = Yup.object().shape(formShape);
  const pickProperties = (values: any) => Lo.pick(values, Lo.keys(formShape));

  const data = rawData ? rawData.getContainerQrcodeById : undefined;

  const initializeNewRecord = async () => {
    setInitialNewRecData({ isActive: true });
  };

  const handleSubmit = async (values: any, actions: any) => {
    actions.setSubmitting(false);
    const mutValues = {
      ...Lo.omit(Lo.pick(values, Lo.keys(formShape)), ['collectionpoint', 'containertype']),
      isActive: values.isActive ? 1 : 0,
    };
    convertFieldToFloat(mutValues, ['latitude', 'longitude']);

    if (isNew) {
      const rec = await addNewRecord({ variables: { data: mutValues } })
        .then((res) => {
          const rec = res.data.addContainerQrcode;
          toastSuccess(`Επιτυχής Προσθήκη Εγγραφής [${rec?.id}]`);
          return rec;
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
          return null;
        });

      if (rec) {
        if (modalOnClose) {
          modalOnClose(rec.id);
        } else {
          history.replace(generatePath(getRouteByName('QrCode'), { id: rec.id }), {
            ...location.state,
          });
        }
      }
    } else {
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((res) => {
          toastSuccess(`Ενημέρωση Εγγραφής [${res.data.updateContainerQrcode.id}]`);
          actions.resetForm({ values: { ...mutValues, id: idNum } });
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  const handleDelete = useCallback(async () => {
    const confirmed = window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε την εγγραφή;');
    if (confirmed) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => handleGraphqlError('mutation-delete', error));
    }
  }, [deleteRecord, idNum]);

  const handleReturn = useCallback(() => {
    const st: any = location?.state || {};

    if (st.fromQrCodesGrid) {
      // came from QR Codes grid
      history.push(generatePath(getRouteByName('ContainersQrCodes')));
      return;
    }

    // default: came from Collection Points grid or elsewhere
    history.push(generatePath(getRouteByName('Collectionpoints')), { ...st });
  }, [history, location.state]);

  useEffect(() => {
    if (isNew) {
      initializeNewRecord();
    } else {
      get({ variables: { id: idNum } });
    }
  }, [get, idNum]);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  }

  if (isNew && !initialNewRecData) {
    return null;
  }

  const initialValues = isNew
    ? initialNewRecData
    : { ...pickProperties(data), isActive: data?.isActive ?? true };

  return (
    <FormCard
      onSubmit={handleSubmit}
      onDelete={hasAccess ? handleDelete : undefined}
      onReturn={!isModal ? handleReturn : null}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      idx={idNum}
      title={isNew ? t('Νέο QR Code') : `${t('QR Code')}: ${data?.qrCode}`}
      permitSubmit={false}
      permitDelete={!isNew}
      injectFooterElements={
        isModal ? () => <ButtonCloseModal onClick={() => modalOnClose?.(-1)} /> : undefined
      }
      loading={loading}
    >
      <QrCodeFormFields isNew={isNew} data={data} />
    </FormCard>
  );
};
